import React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
import { useMutation } from '@apollo/client/react'
import { useDispatch } from 'react-redux'
// components
import Grid from '@mui/material/Grid2'
import SetLicencingModal from 'components/Modals/SetLicencingModal'
import Licence from './Licence'
import BottomPaper from '../BottomPaper'
// constants
import { Forms, Modals } from 'constants/index'
import { LICENCES_NAMES } from 'constants/licences'
// graphql
import { COMPANY_UPDATE_MUTATION } from 'graphql/company/mutations'
// hooks
import { useCurrentUser, useShowModal } from 'hooks/index'
// redux
import { setCurrentUser } from 'store/reducers/currentUser'
import { setAccumulatedFormData } from 'store/reducers/accumulatedForms'
// themes
import { FormInnerWrapper } from 'themes/views/settings'
// utils
import get from 'lodash/get'
import { handleError } from 'utils/errors/handlers'

const LICENCES = [
  { type: LICENCES_NAMES.STRATEGY, name: 'Strategy & Planning' },
  {
    type: LICENCES_NAMES.FORMS,
    name: 'Forms'
  }
]

interface Props {
  goNextTab: () => void
  accountForm: () => void
  prepareForApi: (data: any) => any
  handleCancelClick: () => void
}

const AccountLicensingForm: React.FC<Props> = ({ goNextTab, accountForm, prepareForApi, handleCancelClick }) => {
  const user = useCurrentUser()
  const router = useRouter()
  const dispatch = useDispatch()

  const [companyUpdateMutation] = useMutation(COMPANY_UPDATE_MUTATION)

  const { showModal } = useShowModal({ modalType: Modals.SET_LICENCING_MODAL })

  const handleSwitchStrategy = (key) => {
    const licences = get(accountForm, 'licences', {})

    dispatch(
      setAccumulatedFormData({
        type: Forms.ACCUMULATED_CREATE_ACCOUNT,
        data: {
          licences: {
            ...licences,
            [key]: {
              ...get(licences, key, {}),
              active: !get(accountForm, `licences[${key}].active`, false)
            }
          }
        }
      })
    )
  }

  const handleSaveClick = async () => {
    const isUpdate = router.query.id

    if (isUpdate) {
      try {
        const updatedCompany = prepareForApi(accountForm)

        await companyUpdateMutation({
          variables: {
            id: router.query.id,
            company: updatedCompany
          }
        })

        if (user.companyId === router.query.id) {
          dispatch(
            setCurrentUser({
              ...user,
              company: { ...user.company, licences: [updatedCompany.licences[0]], logoUrl: updatedCompany.logoUrl }
            })
          )
        }

        handleCancelClick()
      } catch (errors) {
        handleError(errors, 'AccountLicensingForm.handleSaveClick')
      }
    } else {
      goNextTab()
    }
  }

  const handleSwitchToggle =
    ({ name, key }) =>
    () => {
      if (get(accountForm, `licences[${key}].licenceType`)) {
        handleSwitchStrategy(key)
      } else {
        showModal({ name, key })
      }
    }

  const handleSetClick =
    ({ name, key }) =>
    () => {
      showModal({ name, key })
    }

  return (
    <>
      <FormInnerWrapper container direction='column' spacing={2}>
        <Grid container>
          {LICENCES.map((license) => (
            <Licence
              licence={license as any}
              key={license.type}
              handleSetClick={handleSetClick}
              handleSwitchToggle={handleSwitchToggle}
            />
          ))}
        </Grid>
      </FormInnerWrapper>
      <BottomPaper onSaveClick={handleSaveClick} onCancelClick={handleCancelClick} />
      <SetLicencingModal handleSwitchStrategy={handleSwitchStrategy} />
    </>
  )
}

export default AccountLicensingForm
